import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eraser } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { OrganizedRoom } from "../types";

interface OrganizeActionsProps {
    selectedItemIds: Set<number>;
    selectedInsurerCount: number;
    selectedContractorCount: number;
    sortedRooms: OrganizedRoom[];
    onMoveToRoom: (itemIds: number[], targetRoomId: number) => void;
    onCreateRoom: (type: 'insurer' | 'contractor') => void;
    onClearSelection: () => void;
}

export const OrganizeActions: React.FC<OrganizeActionsProps> = ({
    selectedItemIds,
    selectedInsurerCount,
    selectedContractorCount,
    sortedRooms,
    onMoveToRoom,
    onCreateRoom,
    onClearSelection
}) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-sm:gap-3">
            {/* Insurer Actions */}
            <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-900 max-sm:flex-col max-sm:items-stretch max-sm:gap-2">
                <Badge variant="outline" className={`whitespace-nowrap h-7 ${selectedInsurerCount > 0 ? "bg-slate-100 dark:bg-slate-800" : "opacity-50"}`}>
                    {selectedInsurerCount} Selected (Ins)
                </Badge>
                <Select value="" disabled={selectedInsurerCount === 0} onValueChange={(val) => {
                    if (val === 'new_insurer') {
                        onCreateRoom('insurer');
                    } else {
                        onMoveToRoom(Array.from(selectedItemIds), Number(val));
                    }
                }}>
                    <SelectTrigger className="h-9 w-full bg-slate-50 dark:bg-slate-950">
                        <SelectValue placeholder="Move to Room..." />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="new_insurer">Create New Insurance Room...</SelectItem>
                        {sortedRooms.filter(r => r.type === 'insurer').map(r => (
                            <SelectItem key={r.id} value={r.id.toString()}>{r.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button
                    size="sm"
                    variant="outline"
                    className="h-9 px-4 max-sm:w-full"
                    onClick={() => onCreateRoom('insurer')}
                >
                    Add Room
                </Button>
            </div>
            {/* Contractor Actions */}
            <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-900 md:col-start-2 max-sm:flex-col max-sm:items-stretch max-sm:gap-2">
                <Badge variant="outline" className={`whitespace-nowrap h-7 ${selectedContractorCount > 0 ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200" : "opacity-50"}`}>
                    {selectedContractorCount} Selected (Con)
                </Badge>
                <Select value="" disabled={selectedContractorCount === 0} onValueChange={(val) => {
                    if (val === 'new_contractor') {
                        onCreateRoom('contractor');
                    } else {
                        onMoveToRoom(Array.from(selectedItemIds), Number(val));
                    }
                }}>
                    <SelectTrigger className="h-9 w-full bg-slate-50 dark:bg-slate-950">
                        <SelectValue placeholder="Move to Room..." />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="new_contractor">Create New Contractor Room...</SelectItem>
                        {sortedRooms.filter(r => r.type === 'contractor').map(r => (
                            <SelectItem key={r.id} value={r.id.toString()}>{r.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button
                    size="sm"
                    variant="outline"
                    className="h-9 px-4 max-sm:w-full"
                    onClick={() => onCreateRoom('contractor')}
                >
                    Add Room
                </Button>
            </div>

            {selectedItemIds.size > 0 && (
                <div className="flex items-center justify-end md:col-span-2">
                    <Button variant="ghost" size="sm" onClick={onClearSelection} className="text-red-500 hover:text-red-600 hover:bg-red-50 h-9 font-medium">
                        <Eraser className="h-4 w-4 mr-2" />
                        Clear Selection
                    </Button>
                </div>
            )}
        </div>
    );
};
